import React, { FC, useMemo, ComponentType } from 'react'
import {
  RichTextBlock as BaseRichTextBlock,
  RichTextSpan,
} from 'prismic-richtext/src/richtext'
import Tree from 'prismic-richtext/src/tree'
import { TextNode } from 'prismic-richtext/src/nodes'

export interface LinkProps {
  link: RichTextLinkSpanData
}

export interface ImageProps {
  image: RichTextImageBlock
}

export type RichTextComponentConfig<
  I extends ImageProps = ImageProps,
  L extends LinkProps = LinkProps
> = {
  heading1: ComponentType
  heading2: ComponentType
  heading3: ComponentType
  heading4: ComponentType
  heading5: ComponentType
  heading6: ComponentType
  paragraph: ComponentType
  preformatted: ComponentType
  strong: ComponentType
  em: ComponentType
  listItem: ComponentType
  oListItem: ComponentType
  list: ComponentType
  oList: ComponentType
  image: ComponentType<I>
  embed: ComponentType
  hyperlink: ComponentType<L>
  label: ComponentType
  span: ComponentType
}

type RichTextNodeType = keyof RichTextComponentConfig

export interface RichTextLinkSpanData {
  link_type: string
  url: string
  target: string
}

export interface RichTextLinkSpan extends RichTextSpan {
  data: RichTextLinkSpanData
}

export interface RichTextImageBlockDimension {
  width: number
  height: number
}

export interface RichTextImageBlock extends BaseRichTextBlock {
  url: string
  alt?: string
  copyright?: string
  dimensions: RichTextImageBlockDimension
}

export type RichTextBlock = BaseRichTextBlock | RichTextImageBlock

export interface PrismicRichTextProps {
  blocks: BaseRichTextBlock[]
  config: RichTextComponentConfig
}

export const PrismicRichText: FC<PrismicRichTextProps> = ({
  blocks,
  config,
}) => {
  const tree = useMemo(() => Tree.fromRichText(blocks), [blocks])
  return (
    <>
      {tree.children.map((c) => (
        <RichTextNode
          key={c.key}
          node={c as TextNode}
          componentConfig={config}
        />
      ))}
    </>
  )
}

interface RichTextNodeProps {
  node: TextNode
  componentConfig: RichTextComponentConfig
}

const RichTextNode: FC<RichTextNodeProps> = ({ node, componentConfig }) => {
  const type = node.element.type as RichTextNodeType
  if (!componentConfig[type]) return null

  const children =
    node.children.length === 0
      ? node.text
      : node.children.map((n) => (
          <RichTextNode
            key={n.key}
            node={n}
            componentConfig={componentConfig}
          />
        ))

  let TargetComponent
  switch (type) {
    case 'heading1':
      TargetComponent = componentConfig[type]
      return <TargetComponent>{children}</TargetComponent>
    case 'paragraph':
      TargetComponent = componentConfig[type]
      return <TargetComponent>{children}</TargetComponent>
    case 'hyperlink':
      TargetComponent = componentConfig[type]
      const link = node.element as RichTextLinkSpan
      return <TargetComponent link={link.data}>{children}</TargetComponent>
    case 'strong':
      TargetComponent = componentConfig[type]
      return <TargetComponent>{children}</TargetComponent>
    case 'em':
      TargetComponent = componentConfig[type]
      return <TargetComponent>{children}</TargetComponent>
    case 'image':
      TargetComponent = componentConfig[type]
      const imageBlock = node.element as RichTextImageBlock
      return <TargetComponent image={imageBlock} />
    // Note: span type doesn't have children
    case 'span':
    default:
      TargetComponent = componentConfig[type]
      return <TargetComponent>{node.element.text}</TargetComponent>
  }
}
